import { msalInstance } from '../auth';
import type { EntraUserSummary, EntraUserDetails, SecurityAlertDetails } from '../../types';
import { ENTRA_ADMIN_USER_URL_PREFIX, SECURITY_ALERT_RECIPIENT } from '../../constants';
import type { GraphCreationOptions, WebAuthnCreationResult } from '../webauthn';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const GRAPH_BETA_BASE = 'https://graph.microsoft.com/beta';

const graphScopes = ['User.Read.All', 'Mail.Send', 'UserAuthMethod-Passkey.ReadWrite.All'];

/**
 * Acquires a Graph-scoped access token from MSAL.
 * Falls back to interactive popup if silent acquisition fails.
 */
async function getGraphToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated account found. Please sign in.');
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: graphScopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch {
    const response = await msalInstance.acquireTokenPopup({ scopes: graphScopes });
    return response.accessToken;
  }
}

/**
 * Makes an authenticated request to Microsoft Graph.
 */
async function graphFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = await getGraphToken();

  const url = path.startsWith('https://') ? path : `${GRAPH_BASE}${path}`;

  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

/**
 * Makes an authenticated request to Microsoft Graph beta endpoint.
 */
async function graphBetaFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = await getGraphToken();

  const url = path.startsWith('https://') ? path : `${GRAPH_BETA_BASE}${path}`;

  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

// ─── FIDO2 Pre-provisioning Operations ───────────────────────────────────────

/**
 * Get WebAuthn creation options for pre-provisioning a FIDO2 key for a user.
 * Uses the Graph beta endpoint with OData function syntax.
 * 
 * @param userPrincipalName - The user's UPN (e.g. user@domain.com). UUID does not work.
 */
export async function getFido2CreationOptions(userPrincipalName: string): Promise<GraphCreationOptions> {
  const path = `/users/${encodeURIComponent(userPrincipalName)}/authentication/fido2Methods/creationOptions(challengeTimeoutInMinutes=5)`;

  const response = await graphBetaFetch(path);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get FIDO2 creation options: ${response.status} ${errorText}`);
  }

  const json = await response.json();
  console.debug('[FIDO2] Raw creationOptions response keys:', Object.keys(json));

  // Normalize: the Graph beta response may or may not have a "value" wrapper.
  // If publicKey exists at the top level, wrap it so downstream code always
  // sees { value: { publicKey, challengeTimeoutDateTime } }.
  if (json.publicKey && !json.value) {
    return { value: json } as GraphCreationOptions;
  }

  return json as GraphCreationOptions;
}

/** Response from Graph when registering a FIDO2 method */
export interface Fido2MethodRegistration {
  id: string;
  displayName: string;
  aaGuid: string;
  model: string | null;
  attestationLevel: string;
  createdDateTime: string;
}

/**
 * Register a FIDO2 credential with Entra ID for a target user.
 * Sends the WebAuthn attestation response to Graph beta.
 */
export async function registerFido2Method(
  userId: string,
  displayName: string,
  credential: WebAuthnCreationResult
): Promise<Fido2MethodRegistration> {
  const path = `/users/${encodeURIComponent(userId)}/authentication/fido2Methods`;
  const body = JSON.stringify({
    displayName,
    publicKeyCredential: {
      id: credential.credentialId,
      response: {
        clientDataJSON: credential.clientDataJSON,
        attestationObject: credential.attestationObject,
      },
      clientExtensionResults: credential.clientExtensionResults,
    },
  });

  const response = await graphBetaFetch(path, { method: 'POST', body });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to register FIDO2 method: ${response.status} ${errorText}`);
  }

  return response.json() as Promise<Fido2MethodRegistration>;
}

// ─── User Operations ─────────────────────────────────────────────────────────

/**
 * Search Entra ID users by display name, email, or UPN.
 */
export async function searchUsers(query: string): Promise<EntraUserSummary[]> {
  if (!query || query.trim().length < 2) return [];

  const sanitizedQuery = query.replace(/'/g, "''");
  const filter = `accountEnabled eq true and (startsWith(displayName,'${sanitizedQuery}') or startsWith(userPrincipalName,'${sanitizedQuery}') or startsWith(mail,'${sanitizedQuery}'))`;
  const select = 'id,displayName,userPrincipalName,mail,accountEnabled,jobTitle,department';

  const response = await graphFetch(
    `/users?$filter=${encodeURIComponent(filter)}&$select=${select}&$top=10`
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`User search failed: ${response.status} ${errorText}`);
  }

  const data = (await response.json()) as { value: EntraUserSummary[] };
  return data.value;
}

/**
 * Fetch profile photo blob URLs for a list of user IDs.
 * Returns a map of userId → blobUrl (missing entries = no photo).
 */
export async function getUserPhotos(userIds: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const results = await Promise.allSettled(
    userIds.map(async (id) => {
      const url = await getUserPhoto(id);
      if (url) map.set(id, url);
    })
  );
  void results; // consume
  return map;
}

/**
 * Get full user details by ID.
 */
export async function getUserById(userId: string): Promise<EntraUserDetails | null> {
  const select = 'id,displayName,userPrincipalName,mail,accountEnabled,jobTitle,department,givenName,surname,officeLocation,mobilePhone';

  const response = await graphFetch(`/users/${encodeURIComponent(userId)}?$select=${select}`);

  if (response.status === 404) return null;

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`User lookup failed: ${response.status} ${errorText}`);
  }

  const user = (await response.json()) as Omit<EntraUserDetails, 'entraAdminUrl' | 'photoUrl'>;

  let photoUrl: string | null = null;
  try {
    photoUrl = await getUserPhoto(user.id);
  } catch {
    // Photo not available — not all users have one
  }

  return {
    ...user,
    entraAdminUrl: `${ENTRA_ADMIN_USER_URL_PREFIX}${user.id}`,
    photoUrl,
  };
}

/**
 * Get a user's profile photo as a blob URL.
 * Returns null if no photo is available.
 */
export async function getUserPhoto(userId: string): Promise<string | null> {
  const token = await getGraphToken();
  const res = await fetch(
    `${GRAPH_BASE}/users/${encodeURIComponent(userId)}/photos/48x48/$value`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Get the currently signed-in admin's profile photo as a blob URL.
 */
export async function getMyPhoto(): Promise<string | null> {
  const token = await getGraphToken();
  const res = await fetch(
    `${GRAPH_BASE}/me/photos/48x48/$value`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ─── Email Operations ────────────────────────────────────────────────────────

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Send the mandatory security alert email after a provisioning action.
 * Sends from the signed-in admin's mailbox via /me/sendMail.
 */
export async function sendSecurityAlertEmail(details: SecurityAlertDetails): Promise<void> {
  const emailBody = buildSecurityAlertEmailBody(details);

  const message = {
    message: {
      subject: `🔐 URGENT: FIDO2 Key Preprovisioned for ${details.targetUserDisplayName}`,
      body: {
        contentType: 'HTML',
        content: emailBody,
      },
      toRecipients: [
        { emailAddress: { address: SECURITY_ALERT_RECIPIENT } },
      ],
      importance: 'high',
    },
    saveToSentItems: true,
  };

  const response = await graphFetch('/me/sendMail', {
    method: 'POST',
    body: JSON.stringify(message),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to send security alert email: ${response.status} ${errorText}`);
  }
}

/**
 * Send a PIN notification email to a specified recipient.
 * Sends from the signed-in admin's mailbox via /me/sendMail.
 *
 * SECURITY NOTE: The PIN is passed only to the email body.
 * It is NOT logged, NOT stored, and NOT included in any audit record.
 */
export async function sendPinNotificationEmail(
  recipientEmail: string,
  pin: string,
  targetUserDisplayName: string,
  keyDisplayName: string
): Promise<void> {
  const message = {
    message: {
      subject: `FIDO2 Security Key PIN for ${targetUserDisplayName}`,
      body: {
        contentType: 'HTML',
        content: `
          <div style="font-family: 'Segoe UI Variable', 'Segoe UI', sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background: #0f6cbd; color: white; padding: 16px 24px; border-radius: 8px 8px 0 0;">
              <h2 style="margin: 0; font-size: 18px; font-weight: 600;">FIDO2 Security Key PIN</h2>
            </div>
            <div style="background: #ffffff; border: 1px solid #e0e0e0; border-top: none; padding: 24px; border-radius: 0 0 8px 8px;">
              <p style="color: #242424; font-size: 14px;">A FIDO2 security key has been preprovisioned for <strong>${escapeHtml(targetUserDisplayName)}</strong>.</p>
              <p style="color: #242424; font-size: 14px;">Key label: <strong>${escapeHtml(keyDisplayName)}</strong></p>
              <div style="background: #f5f5f5; padding: 16px; border-radius: 8px; margin: 16px 0;">
                <p style="margin: 0; font-size: 14px; color: #616161;">Your PIN:</p>
                <p style="margin: 8px 0 0; font-size: 24px; font-family: 'Cascadia Code', Consolas, monospace; font-weight: bold; color: #242424; letter-spacing: 2px;">${escapeHtml(pin)}</p>
              </div>
              <p style="color: #b10e1c; font-weight: 600; font-size: 14px;">⚠️ Please save this PIN securely. This email should be deleted after use.</p>
              <p style="color: #707070; font-size: 12px;">This is an automated message from the FIDO2 Pre-provisioner tool.</p>
            </div>
          </div>
        `,
      },
      toRecipients: [
        { emailAddress: { address: recipientEmail } },
      ],
      importance: 'normal',
    },
    saveToSentItems: false,
  };

  const response = await graphFetch('/me/sendMail', {
    method: 'POST',
    body: JSON.stringify(message),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to send PIN notification email: ${response.status} ${errorText}`);
  }
}

// ─── Security Alert Email Template ───────────────────────────────────────────

function buildSecurityAlertEmailBody(details: SecurityAlertDetails): string {
  return `
    <div style="font-family: 'Segoe UI Variable', 'Segoe UI', sans-serif; max-width: 700px; margin: 0 auto;">
      <div style="background: #b10e1c; color: white; padding: 16px 24px; border-radius: 8px 8px 0 0;">
        <h1 style="margin: 0; font-size: 18px; font-weight: 600;">🔐 FIDO2 Key Preprovisioning Alert</h1>
        <p style="margin: 4px 0 0; font-size: 13px; opacity: 0.9;">Urgent Security Notification</p>
      </div>

      <div style="background: #ffffff; border: 1px solid #e0e0e0; padding: 24px; border-radius: 0 0 8px 8px;">
        <p style="color: #b10e1c; font-weight: 600; font-size: 14px; margin-top: 0;">
          A FIDO2 security key has been preprovisioned. Review the details below and take action if this was not authorized.
        </p>

        <table style="width: 100%; border-collapse: collapse; font-size: 14px; color: #242424;">
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; width: 200px; color: #616161;">Timestamp</td>
            <td style="padding: 10px 12px;">${escapeHtml(details.timestamp)}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; color: #616161;">Admin (Performer)</td>
            <td style="padding: 10px 12px;">${escapeHtml(details.adminDisplayName)} (${escapeHtml(details.adminUpn)})</td>
          </tr>
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; color: #616161;">Target User</td>
            <td style="padding: 10px 12px;">${escapeHtml(details.targetUserDisplayName)}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; color: #616161;">Target UPN</td>
            <td style="padding: 10px 12px;">${escapeHtml(details.targetUserUpn)}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; color: #616161;">Target Email</td>
            <td style="padding: 10px 12px;">${escapeHtml(details.targetUserEmail || 'N/A')}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; color: #616161;">Key Display Name</td>
            <td style="padding: 10px 12px;">${escapeHtml(details.keyDisplayName)}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; color: #616161;">Correlation ID</td>
            <td style="padding: 10px 12px; font-family: 'Cascadia Code', Consolas, monospace; font-size: 12px;">${escapeHtml(details.correlationId)}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; color: #616161;">Tenant ID</td>
            <td style="padding: 10px 12px; font-family: 'Cascadia Code', Consolas, monospace; font-size: 12px;">${escapeHtml(details.tenantId)}</td>
          </tr>
          ${details.notes ? `
          <tr style="border-bottom: 1px solid #e0e0e0;">
            <td style="padding: 10px 12px; font-weight: 600; color: #616161;">Admin Notes</td>
            <td style="padding: 10px 12px;">${escapeHtml(details.notes)}</td>
          </tr>
          ` : ''}
        </table>

        <div style="margin-top: 24px; padding: 16px; background: #fdeded; border-radius: 8px; border-left: 4px solid #b10e1c;">
          <p style="margin: 0 0 8px; font-weight: 600; color: #b10e1c;">Immediate Action Required?</p>
          <p style="margin: 0; font-size: 13px; color: #242424;">
            If this action was not expected, review the user account immediately:
          </p>
          <p style="margin: 8px 0 0;">
            <a href="${escapeHtml(details.entraAdminUrl)}" style="background: #b10e1c; color: white; padding: 8px 16px; border-radius: 8px; text-decoration: none; font-weight: 600; display: inline-block;">
              🔗 View User in Entra Admin Center
            </a>
          </p>
        </div>
      </div>
    </div>
  `;
}
